import { useRef, useState } from 'react';
import {
  Tabs,
  Card,
  Select,
  Button,
  Upload,
  Table,
  Alert,
  Space,
  Typography,
  Steps,
  App,
  Tag,
  Progress,
  Checkbox,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  ExportOutlined,
  ImportOutlined,
  FilterOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { trpc } from '../lib/api';
import type { UploadProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader, PageContainer } from '../components/common';
import { getMutationPendingState } from '../utils/trpcMutation';

const { Title, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

type MasterDataType = 'locations' | 'equipments' | 'parts' | 'suppliers';

type PreviewRow = Record<string, string | number | boolean | null | undefined>;

interface PreviewData {
  headers: string[];
  rows: PreviewRow[];
  totalRows: number;
}

interface ImportError {
  row: number;
  field?: string;
  message: string;
}

interface ImportResult {
  success: boolean;
  importedCount: number;
  errors: ImportError[];
  message?: string;
}

interface ExportFilters {
  locationLevels?: number[];
  locationIds?: string[];
  equipmentCategoryIds?: string[];
  equipmentStatuses?: string[];
  importanceLevels?: number[];
  partCategoryIds?: string[];
  supplierCategoryIds?: string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizePreviewData = (value: unknown): PreviewData | null => {
  if (!isRecord(value)) {
    return null;
  }

  return {
    headers: Array.isArray(value.headers) ? value.headers.filter((header): header is string => typeof header === 'string') : [],
    rows: Array.isArray(value.rows) ? (value.rows.filter(isRecord) as PreviewRow[]) : [],
    totalRows: typeof value.totalRows === 'number' ? value.totalRows : 0,
  };
};

const normalizeImportResult = (value: unknown): ImportResult | null => {
  if (!isRecord(value)) {
    return null;
  }

  const errors = Array.isArray(value.errors)
    ? value.errors.flatMap((error) => {
      if (!isRecord(error) || typeof error.row !== 'number' || typeof error.message !== 'string') {
        return [];
      }
      return [{
        row: error.row,
        field: typeof error.field === 'string' ? error.field : undefined,
        message: error.message,
      }];
    })
    : [];

  return {
    success: typeof value.success === 'boolean' ? value.success : false,
    importedCount: typeof value.importedCount === 'number' ? value.importedCount : 0,
    errors,
    message: typeof value.message === 'string' ? value.message : undefined,
  };
};

const isArrayBuffer = (value: unknown): value is ArrayBuffer =>
  Object.prototype.toString.call(value) === '[object ArrayBuffer]';

const arrayBufferToBase64 = (arrayBuffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
};

export function ImportExportPage() {
  const [activeTab, setActiveTab] = useState<'import' | 'export'>('import');

  return (
    <PageContainer>
      <PageHeader
        title="マスターデータ管理"
        subtitle="CSVファイルを使用して、エリア、設備、部品・消耗品、業者のマスターデータをインポート・エクスポートできます。"
        icon={<DatabaseOutlined />}
      />

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as 'import' | 'export')}
        items={[
          {
            key: 'import',
            label: (
              <span>
                <ImportOutlined />
                インポート
              </span>
            ),
            children: <ImportTab />,
          },
          {
            key: 'export',
            label: (
              <span>
                <ExportOutlined />
                エクスポート
              </span>
            ),
            children: <ExportTab />,
          },
        ]}
      />
    </PageContainer>
  );
}

// ========================================
// インポートタブ
// ========================================
function ImportTab() {
  const { message } = App.useApp();
  const [masterDataType, setMasterDataType] = useState<MasterDataType>('locations');
  const [currentStep, setCurrentStep] = useState(0);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [uploadInputKey, setUploadInputKey] = useState(0);
  const uploadVersionRef = useRef(0);

  // テンプレートダウンロード
  const downloadTemplateQuery = trpc.import.getMasterTemplate.useQuery(
    { type: masterDataType },
    {
      enabled: false,
    }
  );

  // プレビュー
  const previewMutation = trpc.import.previewMasterData.useMutation();

  // インポート実行
  const executeMutation = trpc.import.executeMasterImport.useMutation();

  const clearLoadedFileState = () => {
    setPreviewData(null);
    setImportResult(null);
    setFileContent('');
    setImportProgress(0);
  };

  const resetUploadInput = () => {
    setUploadInputKey((key) => key + 1);
  };

  // ファイルアップロード設定
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.csv',
    beforeUpload: (file) => {
      const uploadVersion = uploadVersionRef.current + 1;
      uploadVersionRef.current = uploadVersion;
      setCurrentStep(0);
      clearLoadedFileState();
      const reader = new FileReader();
      reader.onload = (e) => {
        if (uploadVersion !== uploadVersionRef.current) return;
        const arrayBuffer = e.target?.result;
        if (!isArrayBuffer(arrayBuffer)) {
          message.error('CSVファイルの読み込みに失敗しました');
          resetUploadInput();
          return;
        }
        const base64 = arrayBufferToBase64(arrayBuffer);
        setFileContent(base64);
        previewMutation.mutate(
          {
            type: masterDataType,
            fileContent: base64,
          },
          {
            onSuccess: (data) => {
              if (uploadVersion !== uploadVersionRef.current) return;
              const normalized = normalizePreviewData(data);
              if (!normalized) {
                message.error('プレビュー形式が不正です');
                return;
              }
              setPreviewData(normalized);
              setCurrentStep(1);
              message.success('プレビューを取得しました');
            },
            onError: (error) => {
              if (uploadVersion !== uploadVersionRef.current) return;
              message.error(`プレビューエラー: ${error.message}`);
            },
          },
        );
        resetUploadInput();
      };
      reader.onerror = () => {
        if (uploadVersion !== uploadVersionRef.current) return;
        message.error('CSVファイルの読み込みに失敗しました');
        resetUploadInput();
      };
      reader.readAsArrayBuffer(file);
      return Upload.LIST_IGNORE;
    },
  };

  const handleDownloadTemplate = async () => {
    try {
      const result = await downloadTemplateQuery.refetch();
      const data = result.data;
      if (!data) {
        message.error('テンプレートの取得に失敗しました');
        return;
      }

      const binaryString = atob(data.content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: data.mimeType });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', data.filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('テンプレートをダウンロードしました');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'テンプレートの取得に失敗しました';
      message.error(`エラー: ${errorMessage}`);
    }
  };

  const handleImport = () => {
    if (!fileContent) return;

    const uploadVersion = uploadVersionRef.current;
    setImporting(true);
    setImportProgress(0);

    // プログレスバーのシミュレーション
    const timer = setInterval(() => {
      setImportProgress((prev) => {
        if (prev >= 95) {
          clearInterval(timer);
          return 95;
        }
        return prev + 5;
      });
    }, 200);

    executeMutation.mutate(
      {
        type: masterDataType,
        fileContent,
      },
      {
        onSuccess: (result) => {
          if (uploadVersion !== uploadVersionRef.current) return;
          const normalized = normalizeImportResult(result);
          if (!normalized) {
            message.error('インポート結果の形式が不正です');
            setImporting(false);
            return;
          }
          setImportResult(normalized);
          setCurrentStep(2);
          setImporting(false);
          setImportProgress(100);
          if (normalized.success) {
            message.success('インポートが完了しました');
          } else {
            message.warning('インポートが完了しましたが、エラーがあります');
          }
        },
        onError: (error) => {
          if (uploadVersion !== uploadVersionRef.current) return;
          message.error(`インポートエラー: ${error.message}`);
          setImporting(false);
        },
        onSettled: () => {
          clearInterval(timer);
        },
      }
    );
  };

  const getMasterDataTypeLabel = (type: MasterDataType) => {
    const labels: Record<MasterDataType, string> = {
      locations: 'エリア',
      equipments: '設備',
      parts: '部品・消耗品',
      suppliers: '業者',
    };
    return labels[type];
  };

  const getPreviewColumns = (): ColumnsType<PreviewRow> => {
    if (!previewData || previewData.headers.length === 0) return [];

    return previewData.headers.map((header) => ({
      title: header,
      dataIndex: header,
      key: header,
      ellipsis: true,
      width: 150,
    }));
  };

  const getErrorColumns = (): ColumnsType<ImportError> => [
    {
      title: '行番号',
      dataIndex: 'row',
      key: 'row',
      width: 100,
    },
    {
      title: 'フィールド',
      dataIndex: 'field',
      key: 'field',
      width: 150,
    },
    {
      title: 'エラー内容',
      dataIndex: 'message',
      key: 'message',
    },
  ];

  const steps = [
    {
      title: 'ファイル選択',
      icon: <UploadOutlined />,
    },
    {
      title: 'プレビュー確認',
      icon: <FileTextOutlined />,
    },
    {
      title: 'インポート完了',
      icon: <CheckCircleOutlined />,
    },
  ];

  return (
    <div>
      <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />

      {currentStep === 0 && (
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text strong>マスターデータタイプ:</Text>
              <Select
                value={masterDataType}
                onChange={setMasterDataType}
                style={{ width: 200, marginLeft: 16 }}
              >
                <Option value="locations">エリア</Option>
                <Option value="equipments">設備</Option>
                <Option value="parts">部品・消耗品</Option>
                <Option value="suppliers">業者</Option>
              </Select>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => {
                  void handleDownloadTemplate();
                }}
                style={{ marginLeft: 16 }}
              >
                テンプレートダウンロード
              </Button>
            </div>

            <Dragger key={uploadInputKey} {...uploadProps} style={{ padding: 40 }}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
              </p>
              <p className="ant-upload-text">
                クリックまたはドラッグしてCSVファイルをアップロード
              </p>
              <p className="ant-upload-hint">
                {getMasterDataTypeLabel(masterDataType)}データのCSVファイルを選択してください
              </p>
            </Dragger>

            <Alert
              message="インポート前の注意事項"
              description={
                <ul style={{ marginBottom: 0 }}>
                  <li>CSVファイルはShift-JISエンコーディングで保存してください</li>
                  <li>ヘッダー行は必須です</li>
                  <li>日付形式: YYYY-MM-DD</li>
                  <li>最大ファイルサイズ: 10MB</li>
                  <li>最大行数: 10,000行</li>
                </ul>
              }
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
            />
          </Space>
        </Card>
      )}

      {currentStep === 1 && previewData && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card title="インポートサマリー">
            <Space size="large">
              <Tag color="blue">
                <FileTextOutlined /> 総行数: {previewData.totalRows}件
              </Tag>
            </Space>
          </Card>

          {previewData.rows.length > 0 && (
            <Card title="データプレビュー（最初の10件）">
              <Table
                columns={getPreviewColumns()}
                dataSource={previewData.rows}
                pagination={false}
                scroll={{ x: 1200 }}
                size="small"
                rowKey={(_, index) => `row-${index}`}
              />
              {previewData.totalRows > 10 && (
                <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                  他 {previewData.totalRows - 10} 件...
                </Text>
              )}
            </Card>
          )}

          <Card>
            <Space>
              <Button
                onClick={() => {
                  uploadVersionRef.current += 1;
                  setCurrentStep(0);
                  clearLoadedFileState();
                  resetUploadInput();
                }}
              >
                戻る
              </Button>
              <Button
                type="primary"
                onClick={handleImport}
                loading={importing}
                disabled={previewData.totalRows === 0}
              >
                インポート実行
              </Button>
            </Space>
          </Card>

          {importing && (
            <Card>
              <Progress percent={importProgress} status="active" />
              <Text>データをインポート中...</Text>
            </Card>
          )}
        </Space>
      )}

      {currentStep === 2 && importResult && (
        <Card>
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            {importResult.success ? (
              <CheckCircleOutlined style={{ fontSize: 72, color: '#52c41a' }} />
            ) : (
              <InfoCircleOutlined style={{ fontSize: 72, color: '#faad14' }} />
            )}
            <Title level={3} style={{ marginTop: 24 }}>
              {importResult.success ? 'インポート完了' : 'インポート完了（一部エラー）'}
            </Title>
            <Text type="secondary">
              {importResult.importedCount}件のデータが正常にインポートされました
            </Text>

            {importResult.errors && importResult.errors.length > 0 && (
              <Card
                title="エラー一覧"
                style={{ marginTop: 24, textAlign: 'left' }}
              >
                <Table
                  columns={getErrorColumns()}
                  dataSource={importResult.errors}
                  pagination={{ pageSize: 10 }}
                  size="small"
                  rowKey={(record, index) => `error-${index}`}
                />
              </Card>
            )}

            <div style={{ marginTop: 24 }}>
              <Space>
                <Button
                  type="primary"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  データ一覧へ
                </Button>
                <Button
                  onClick={() => {
                    uploadVersionRef.current += 1;
                    setCurrentStep(0);
                    clearLoadedFileState();
                    resetUploadInput();
                  }}
                >
                  新規インポート
                </Button>
              </Space>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ========================================
// エクスポートタブ
// ========================================
function ExportTab() {
  const { message } = App.useApp();
  const [masterDataType, setMasterDataType] = useState<MasterDataType>('locations');
  const [filters, setFilters] = useState<ExportFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const exportCountQuery = trpc.import.getExportCount.useQuery({
    type: masterDataType,
    filters,
  });
  const exportCount = exportCountQuery.data?.count ?? 0;

  // エクスポート実行
  const exportMutation = trpc.import.exportMasterData.useMutation({
    onSuccess: (data) => {
      // Base64デコードしてダウンロード
      const binaryString = atob(data.content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: data.mimeType });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', data.filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success(`${data.recordCount}件のデータをエクスポートしました`);
    },
    onError: (error) => {
      message.error(`エクスポートエラー: ${error.message}`);
    },
  });

  const handleExport = () => {
    exportMutation.mutate({
      type: masterDataType,
      filters,
    });
  };


  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>マスターデータタイプ:</Text>
            <Select
              value={masterDataType}
              onChange={(value) => {
                setMasterDataType(value);
                setFilters({});
              }}
              style={{ width: 200, marginLeft: 16 }}
            >
              <Option value="locations">エリア</Option>
              <Option value="equipments">設備</Option>
              <Option value="parts">部品・消耗品</Option>
              <Option value="suppliers">業者</Option>
            </Select>
          </div>

          <div>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'フィルターを隠す' : 'フィルターを表示'}
            </Button>
          </div>

          {showFilters && (
            <Card title="フィルター設定" size="small">
              {masterDataType === 'locations' && (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text>レベル:</Text>
                    <Checkbox.Group
                      options={[
                        { label: 'レベル0', value: 0 },
                        { label: 'レベル1', value: 1 },
                        { label: 'レベル2', value: 2 },
                        { label: 'レベル3', value: 3 },
                      ]}
                      value={filters.locationLevels}
                      onChange={(values) =>
                        setFilters({ ...filters, locationLevels: values as number[] })
                      }
                      style={{ marginLeft: 16 }}
                    />
                  </div>
                </Space>
              )}

              {masterDataType === 'equipments' && (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text>ステータス:</Text>
                    <Checkbox.Group
                      options={[
                        { label: '稼働中', value: 'active' },
                        { label: '停止中', value: 'inactive' },
                        { label: 'メンテナンス中', value: 'maintenance' },
                      ]}
                      value={filters.equipmentStatuses}
                      onChange={(values) =>
                        setFilters({ ...filters, equipmentStatuses: values as string[] })
                      }
                      style={{ marginLeft: 16 }}
                    />
                  </div>
                  <div>
                    <Text>重要度:</Text>
                    <Checkbox.Group
                      options={[
                        { label: '低', value: 1 },
                        { label: '中', value: 2 },
                        { label: '高', value: 3 },
                        { label: '最重要', value: 4 },
                      ]}
                      value={filters.importanceLevels}
                      onChange={(values) =>
                        setFilters({ ...filters, importanceLevels: values as number[] })
                      }
                      style={{ marginLeft: 16 }}
                    />
                  </div>
                </Space>
              )}
            </Card>
          )}
        </Space>
      </Card>

      <Card title="エクスポートプレビュー">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Tag color="blue" style={{ fontSize: 16, padding: '8px 16px' }}>
              <FileTextOutlined /> エクスポート件数: {exportCount}件
            </Tag>
          </div>

          {exportCount === 0 && (
            <Alert
              message="エクスポートするデータがありません"
              description="フィルター条件を変更するか、データを登録してください。"
              type="warning"
              showIcon
            />
          )}

          <div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={getMutationPendingState(exportMutation)}
              disabled={exportCount === 0}
              size="large"
            >
              エクスポート実行
            </Button>
          </div>

          <Alert
            message="エクスポート形式"
            description={
              <ul style={{ marginBottom: 0 }}>
                <li>ファイル形式: CSV（Shift-JIS）</li>
                <li>Excelで直接開けます</li>
                <li>エクスポートしたファイルは編集後、再インポート可能です</li>
              </ul>
            }
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
          />
        </Space>
      </Card>
    </Space>
  );
}
